import React, { useState, useEffect, createContext, useContext } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  TrendingUp,
  Receipt,
  FileText,
  MessageSquare,
  Settings,
  LogOut,
  Coffee,
  ChevronDown,
  ChevronRight,
  Menu,
  Users,
  Package,
  UserCog,
  Megaphone,
  ChefHat,
  ShieldCheck,
  Store,
  BarChart3,
  Sparkles,
  StickyNote,
  FolderOpen,
  Target,
  Mail,
  LifeBuoy,
  Banknote,
  AlertCircle,
  Wallet,
  Inbox,
  Shield,
  Upload as UploadIcon,
  BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import { useModules } from '../context/ModulesContext';
import { useAdmin } from '../context/AdminContext';

// Navigation context for AI Advisor
const NavigationContext = createContext({ currentSection: 'operations' });
export const useNavigationContext = () => useContext(NavigationContext);

// Navigation structure with categories
const navCategories = [
  {
    id: 'operations',
    label: 'Operations',
    icon: BarChart3,
    items: [
      { path: '/imports', icon: UploadIcon, label: 'Imports' },
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/pl-report', icon: FileText, label: 'P&L Report' },
      { path: '/expenses', icon: Receipt, label: 'Expenses' },
      { path: '/smart-invoice', icon: Sparkles, label: 'Smart Invoice' },
      { path: '/documents', icon: FolderOpen, label: 'Documents' },
      { path: '/projects', icon: Target, label: 'Projects' },
      { path: '/board-meeting', icon: Users, label: 'Board Meeting' },
      { path: '/email', icon: Mail, label: 'Email' },
      { path: '/rescue-plan', icon: LifeBuoy, label: 'Rescue Plan' },
      { path: '/bank-statements', icon: Banknote, label: 'Bank Statements' },
      { path: '/debts', icon: AlertCircle, label: 'Debts' },
    ],
    aiContext: {
      focus: 'P&L analysis, cost control, profitability',
      prompts: [
        'Why did margins change this week?',
        'Compare this month to last month',
        'What are my biggest expenses?'
      ]
    }
  },
  {
    id: 'kitchen',
    label: 'Kitchen',
    icon: ChefHat,
    items: [
      { path: '/product-mix', icon: Package, label: 'Product Mix' },
      { path: '/ingredients', icon: Package, label: 'Ingredients' },
      { path: '/inventory', icon: Package, label: 'Inventory (Square)' },
      { path: '/recipes', icon: ChefHat, label: 'Recipes' },
      { path: '/health-safety', icon: ShieldCheck, label: 'Health & Safety' },
    ],
    aiContext: {
      focus: 'Menu optimization, recipes, ingredient costs, food cost %',
      prompts: [
        'Which menu items have the highest food cost?',
        'What items are underpriced?',
        'Show my most profitable items'
      ]
    }
  },
  {
    id: 'front-of-house',
    label: 'Front of House',
    icon: Store,
    items: [
      { path: '/revenue', icon: TrendingUp, label: 'Revenue' },
      { path: '/front-of-house', icon: Users, label: 'Orders' },
      { path: '/staff', icon: UserCog, label: 'Staff' },
      { path: '/payroll', icon: Wallet, label: 'Payroll' },
    ],
    aiContext: {
      focus: 'Server performance, peak hours, revenue drivers',
      prompts: [
        'Who is my best server?',
        'When should I add more staff?',
        'What are my peak hours?'
      ]
    }
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    items: [
      { path: '/marketing/deals-calendar', icon: Sparkles, label: 'Deals Calendar' },
      { path: '/marketing/deals-calendar/library', icon: BookOpen, label: 'Deal Library' },
      { path: '/marketing/reviews', icon: MessageSquare, label: 'Reviews', badge: 'reviewsNegative' },
    ],
    aiContext: {
      focus: 'Deal planning, promo trends, customer insight',
      prompts: [
        'What deal would fill our Tuesdays?',
        'Suggest a bundle to lift lunch spend',
      ]
    }
  },
];

// Feature flag → nav path mapping (paths that require feature to be enabled)
const FEATURE_GATED_PATHS = {
  '/board-meeting': 'board_meeting',
  '/projects': 'ai_projects',
  '/ai-chat': 'ai_chat',
  '/smart-invoice': 'smart_invoice',
  '/email': 'email',
};

// Collapsible Nav Section Component
const NavSection = ({ category, isExpanded, onToggle, onClose, features, badges = {} }) => {
  const location = useLocation();
  const { isRouteEnabled } = useModules();
  // Filter items by feature flags AND enabled modules.
  // Both must allow the item to be visible.
  const visibleItems = category.items.filter(item => {
    const featureKey = FEATURE_GATED_PATHS[item.path];
    const featureOK = !featureKey || features[featureKey] !== false;
    const moduleOK = isRouteEnabled(item.path);
    return featureOK && moduleOK;
  });
  const isActive = visibleItems.some(item => location.pathname === item.path);
  const sectionBadge = visibleItems.reduce((n, item) => n + (item.badge ? badges[item.badge] || 0 : 0), 0);

  // If a category has zero visible items after module filtering, hide the whole group.
  if (!category.comingSoon && visibleItems.length === 0) {
    return null;
  }
  
  if (category.comingSoon) {
    return (
      <div className="mb-1">
        <div 
          className={cn(
            "flex items-center justify-between px-3 py-2.5 rounded-md text-muted-foreground cursor-not-allowed opacity-60"
          )}
        >
          <div className="flex items-center gap-3">
            <category.icon className="w-5 h-5" />
            <span className="font-medium text-sm">{category.label}</span>
          </div>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">Soon</Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          isActive && "bg-accent/50 text-accent-foreground font-medium"
        )}
        data-testid={`nav-section-${category.id}`}
      >
        <div className="flex items-center gap-3">
          <category.icon className="w-5 h-5" />
          <span className="font-medium text-sm">{category.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {!isExpanded && sectionBadge > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] text-center" data-testid={`badge-section-${category.id}`}>
              {sectionBadge}
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </div>
      </button>
      
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-border pl-3">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  isActive 
                    ? "bg-primary/10 text-primary font-medium" 
                    : "text-muted-foreground"
                )
              }
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              {item.badge && badges[item.badge] > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] text-center" data-testid={`badge-${item.path.slice(1)}`}>
                  {badges[item.badge]}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

// Floating AI Advisor Button
const FloatingAIAdvisor = ({ currentSection }) => {
  const navigate = useNavigate();
  const category = navCategories.find(c => c.id === currentSection);
  const [showTooltip, setShowTooltip] = useState(false);
  
  const handleClick = () => {
    // Navigate to AI chat with context
    navigate('/ai-chat', { 
      state: { 
        context: currentSection,
        focus: category?.aiContext?.focus,
        suggestedPrompts: category?.aiContext?.prompts 
      } 
    });
  };

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
      {showTooltip && category && (
        <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-card border rounded-lg shadow-lg animate-in fade-in slide-in-from-bottom-2 hidden sm:block">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm">AI Advisor</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            {category.aiContext?.focus}
          </p>
          <div className="space-y-1">
            {category.aiContext?.prompts?.slice(0, 2).map((prompt, i) => (
              <p key={i} className="text-xs text-muted-foreground italic">
                &ldquo;{prompt}&rdquo;
              </p>
            ))}
          </div>
        </div>
      )}
      <Button
        size="lg"
        className="h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        data-testid="floating-ai-advisor"
      >
        <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
      </Button>
    </div>
  );
};

// NavContent as a separate component that receives props
const NavContent = ({ 
  currentLocation, 
  locations, 
  setCurrentLocation, 
  user, 
  expandedSections, 
  toggleSection, 
  handleLogout,
  onClose,
  features,
  branding,
  badges = {},
  isPlatformAdmin = false,
}) => (
  <div className="flex flex-col h-full">
    {/* Logo — org branding or Tillytics default */}
    <div className="p-4 border-b">
      <div className="flex items-center gap-3">
        <img 
          src={branding?.logo_url || "https://static.prod-images.emergentagent.com/jobs/681723f4-949e-4dfb-a708-ae22870c487f/images/fc4c8d5b222cdc512f6e5f2e5185c2475b7c2e672eff6498f61984fab79c4fbe.png"}
          alt={branding?.org_name || "Tillytics"}
          className="w-10 h-10 object-contain"
        />
        <div>
          <span className="text-lg font-bold font-heading block leading-tight">{branding?.org_name || "Tillytics"}</span>
          <span className="text-xs text-muted-foreground">{branding?.tagline || "Till Analytics"}</span>
        </div>
      </div>
    </div>

    {/* Location Selector */}
    {currentLocation && (
      <div className="p-4 border-b">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="w-full justify-between text-left font-normal"
              data-testid="location-selector"
            >
              <div className="truncate">
                <p className="text-xs text-muted-foreground">Location</p>
                <p className="font-medium truncate">{currentLocation.name}</p>
              </div>
              <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuLabel>Switch Location</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {locations.map((loc) => (
              <DropdownMenuItem
                key={loc.id}
                onClick={() => setCurrentLocation(loc)}
                className={loc.id === currentLocation.id ? 'bg-accent' : ''}
                data-testid={`location-option-${loc.id}`}
              >
                {loc.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )}

    {/* Navigation Categories */}
    <ScrollArea className="flex-1 px-3 py-4">
      <nav className="space-y-1">
        {navCategories.map((category) => (
          <NavSection
            key={category.id}
            category={category}
            isExpanded={expandedSections[category.id]}
            onToggle={() => toggleSection(category.id)}
            onClose={onClose}
            features={features}
            badges={badges}
          />
        ))}
        
        {/* Settings + bottom links - standalone */}
        <div className="pt-4 mt-4 border-t">
          {/* Inbox — moved out of Operations so it lives right above Settings */}
          <NavLink
            to="/inbox"
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                isActive
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground"
              )
            }
            data-testid="nav-inbox"
          >
            <Inbox className="w-5 h-5" />
            <span className="font-medium text-sm flex-1">Inbox</span>
            {badges.inboxPending > 0 && (
              <span
                className="bg-red-500 text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] text-center"
                data-testid="badge-inbox"
              >
                {badges.inboxPending}
              </span>
            )}
          </NavLink>

          <NavLink
            to="/settings"
            onClick={onClose}
            title="Settings"
            aria-label="Settings"
            className={({ isActive }) =>
              cn(
                "inline-flex items-center justify-center p-2 rounded-md transition-colors mt-1",
                "hover:bg-accent hover:text-accent-foreground",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground"
              )
            }
            data-testid="nav-settings"
          >
            <Settings className="w-5 h-5" />
          </NavLink>
          
          {/* Notes - under Settings area */}
          <NavLink
            to="/notes"
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                isActive 
                  ? "bg-primary/10 text-primary font-medium" 
                  : "text-muted-foreground"
              )
            }
            data-testid="nav-notes"
          >
            <StickyNote className="w-5 h-5" />
            <span className="font-medium text-sm">All Notes</span>
          </NavLink>

          {/* Platform Admin — only shown to users in PLATFORM_ADMIN_EMAILS */}
          {isPlatformAdmin && (
            <>
              <NavLink
                to="/admin/orgs"
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                    (isActive || location.pathname === '/admin/orgs' || location.pathname.startsWith('/admin/orgs/'))
                      ? "bg-amber-500/10 text-amber-700 font-medium"
                      : "text-muted-foreground"
                  )
                }
                data-testid="nav-admin"
              >
                <Shield className="w-5 h-5" />
                <span className="font-medium text-sm">Admin</span>
                <Badge variant="outline" className="ml-auto text-[10px] py-0">Platform</Badge>
              </NavLink>
              <NavLink
                to="/admin/invite-codes"
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 pl-9 pr-3 py-2 rounded-md transition-colors text-sm",
                    "hover:bg-accent hover:text-accent-foreground",
                    isActive
                      ? "bg-amber-500/10 text-amber-700 font-medium"
                      : "text-muted-foreground"
                  )
                }
                data-testid="nav-invite-codes"
              >
                <span>Invite codes</span>
              </NavLink>
            </>
          )}
        </div>
      </nav>
    </ScrollArea>

    {/* User Menu */}
    <div className="p-4 border-t">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-full justify-start gap-3 h-auto py-2"
            data-testid="user-menu-trigger"
          >
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-primary-foreground">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="text-left flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
);

const Sidebar = ({ mobile = false, onClose, branding }) => {
  const { currentLocation, locations, setCurrentLocation, logout, user, api } = useAuth();
  const { isPlatformAdmin } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [features, setFeatures] = useState({});
  const [badges, setBadges] = useState({ inboxPending: 0 });

  // Fetch feature flags
  useEffect(() => {
    api.get('/features').then(res => setFeatures(res.data)).catch(() => {});
  }, [api]);

  // Poll inbox counts every 60s — drives the "Inbox" badge in the nav
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/inbox');
        if (!cancelled) setBadges((b) => ({ ...b, inboxPending: res.data?.counts?.needs_review || 0 }));
      } catch {/* silent */}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [api]);

  // Negative (1-3★) review drafts awaiting approval — drives the "Reviews" badge
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/gbp/alerts/badge');
        if (!cancelled) setBadges((b) => ({ ...b, reviewsNegative: res.data?.negative_pending || 0 }));
      } catch {/* silent */}
    };
    load();
    const id = setInterval(load, 60000);
    window.addEventListener('gbp-badge-refresh', load);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('gbp-badge-refresh', load); };
  }, [api]);
  
  // Determine which sections should be expanded based on current route
  const getInitialExpanded = () => {
    const expanded = {};
    navCategories.forEach(cat => {
      const isActive = cat.items.some(item => location.pathname === item.path);
      expanded[cat.id] = isActive;
    });
    // Default to operations expanded if nothing else is
    if (!Object.values(expanded).some(v => v)) {
      expanded['operations'] = true;
    }
    return expanded;
  };
  
  const [expandedSections, setExpandedSections] = useState(getInitialExpanded);
  
  // Update expanded sections when route changes
  useEffect(() => {
    navCategories.forEach(cat => {
      const isActive = cat.items.some(item => location.pathname === item.path);
      if (isActive) {
        setExpandedSections(prev => ({ ...prev, [cat.id]: true }));
      }
    });
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const navContentProps = {
    currentLocation,
    locations,
    setCurrentLocation,
    user,
    expandedSections,
    toggleSection,
    handleLogout,
    onClose,
    features,
    branding,
    badges,
    isPlatformAdmin,
  };

  if (mobile) {
    return <NavContent {...navContentProps} />;
  }

  return (
    <aside className="hidden lg:flex lg:flex-col lg:w-64 border-r bg-card h-screen sticky top-0">
      <NavContent {...navContentProps} />
    </aside>
  );
};

// Determine current section from pathname
const getSectionFromPath = (pathname) => {
  for (const category of navCategories) {
    if (category.items.some(item => item.path === pathname)) {
      return category.id;
    }
  }
  return 'operations'; // default
};

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [branding, setBranding] = React.useState(null);
  const { api } = useAuth();
  const location = useLocation();
  const currentSection = getSectionFromPath(location.pathname);

  React.useEffect(() => {
    api.get('/branding').then(res => setBranding(res.data)).catch(() => {});
  }, [api]);
  
  // Don't show floating AI on the AI chat page itself
  const showFloatingAI = location.pathname !== '/ai-chat';

  return (
    <NavigationContext.Provider value={{ currentSection }}>
      <div className="flex min-h-screen bg-background">
        {/* Desktop Sidebar */}
        <Sidebar branding={branding} />

        {/* Mobile Header & Sidebar */}
        <div className="flex-1 flex flex-col">
          <header className="lg:hidden glass-header sticky top-0 z-40">
            <div className="flex items-center justify-between px-4 h-16">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                  <Coffee className="w-4 h-4 text-primary-foreground" />
                </div>
                <span className="font-bold font-heading">{branding?.org_name || "Tillytics"}</span>
              </div>
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-72">
                  <Sidebar mobile onClose={() => setMobileOpen(false)} branding={branding} />
                </SheetContent>
              </Sheet>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1">
            {children}
          </main>
        </div>
        
        {/* Floating AI Advisor */}
        {showFloatingAI && <FloatingAIAdvisor currentSection={currentSection} />}
      </div>
    </NavigationContext.Provider>
  );
};

export default Layout;
